import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environments/environment';
import { Observable, catchError, map, of } from 'rxjs';
import { KNOWN_ROUTE_SEGMENTS } from '../../app-routing.module';
import { BaseConnectionService } from './base-connection.service';
import { ConfigurationService } from './configuration.service';

/**
 * Service for managing group link state and validation.
 * Group links allow admins to create custom webapp versions accessible via URL path prefixes.
 */
@Injectable({
  providedIn: 'root',
})
export class GroupLinkService extends BaseConnectionService {
  private groupLink: string | null = null;
  private http = inject(HttpClient);
  private configurationService = inject(ConfigurationService);

  constructor() {
    super();
    this.loadFromSessionStorage();
  }

  /**
   * Returns the client-specific session storage key.
   * Format: gs-group-link-{client} (e.g., gs-group-link-mets)
   */
  private getSessionStorageKey(): string {
    const client = this.configurationService.environment || 'default';
    return `gs-group-link-${client}`;
  }

  /**
   * Extracts the first URL segment and determines if it's a group link.
   * A segment is considered a group link if it doesn't match any known route segments.
   *
   * @param url - The URL to extract the group link from
   * @returns The group link if found, null otherwise
   */
  extractGroupLinkFromUrl(url: string): string | null {
    const segments = url.split('/').filter(segment => segment.length > 0);

    if (segments.length === 0) {
      return null;
    }

    const firstSegment = segments[0];

    // Check if the first segment matches any known route
    const isKnownRoute = KNOWN_ROUTE_SEGMENTS.includes(firstSegment);

    return isKnownRoute ? null : firstSegment;
  }

  /**
   * Validates a group link with the backend.
   *
   * @param groupLink - The group link to validate
   * @returns Observable that emits true if valid, false otherwise
   */
  validateGroupLink(groupLink: string): Observable<boolean> {
    const endpoint = `${environment.apiUrl}/group-links/${groupLink}`;
    return this.http.get(endpoint).pipe(
      map(() => true),
      catchError((error: HttpErrorResponse) => {
        // Return false for any error (403, 404, network errors, etc.)
        return of(false);
      })
    );
  }

  /**
   * Stores the group link in memory and session storage.
   *
   * @param groupLink - The group link to store
   */
  setGroupLink(groupLink: string): void {
    this.groupLink = groupLink;
    sessionStorage.setItem(this.getSessionStorageKey(), groupLink);
  }

  /**
   * Removes the group link from memory and session storage.
   */
  clearGroupLink(): void {
    this.groupLink = null;
    sessionStorage.removeItem(this.getSessionStorageKey());
  }

  /**
   * Restores the group link from session storage on service initialization.
   */
  private loadFromSessionStorage(): void {
    const storedGroupLink = sessionStorage.getItem(this.getSessionStorageKey());
    if (storedGroupLink) {
      this.groupLink = storedGroupLink;
    }
  }

  /**
   * Prepends the group link to the given route segments if a group link is active.
   *
   * @param segments - The route segments
   * @returns The updated route segments
   */
  prependGroupLink(segments: (string | number)[]): (string | number)[] {
    if (this.groupLink) {
      const updatedSegments = [...segments];
      let isAbsolute = false;

      // Handle absolute paths starting with '/' or empty string
      if (
        updatedSegments.length > 0 &&
        typeof updatedSegments[0] === 'string' &&
        (updatedSegments[0].startsWith('/') || updatedSegments[0] === '')
      ) {
        isAbsolute = true;
        if (updatedSegments[0].startsWith('/')) {
          updatedSegments[0] = updatedSegments[0].substring(1);
        }
        if (updatedSegments[0] === '') {
          updatedSegments.shift();
        }
      }

      // Check if the group link is already the first segment to avoid duplicates
      if (
        updatedSegments.length > 0 &&
        typeof updatedSegments[0] === 'string' &&
        updatedSegments[0].startsWith(this.groupLink)
      ) {
        return isAbsolute ? ['/', ...updatedSegments] : [...updatedSegments];
      }

      return isAbsolute
        ? ['/', this.groupLink, ...updatedSegments]
        : [this.groupLink, ...updatedSegments];
    }

    return segments;
  }

  /**
   * Returns the current group link value.
   *
   * @returns The current group link or null if not set
   */
  getGroupLink(): string | null {
    return this.groupLink;
  }
}
